import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CircularProgress, Grid, Typography } from "@mui/material";
import { PageWithTabs } from "components/pageWithTabs";
import { MembershipInfoColumn } from "components/membershipInfoColumn";
import { IMembership } from "reducers/memberships";
import { MembershipsContext } from "contexts/memberships";
import { prettifyOrgNumber } from "utils/helpers";
import { MembershipAdjustmentsContent } from "components/membershipAdjustmentsContent";
import { usePaginationStore } from "utils/usePaginationStore";
import { InvoiceOrdersTable } from "components/tableComponents/invoiceOrdersTable";

export const MembershipView: FC = () => {
    const { fetchMembership, updateMembership, terminateMembership, reactivateMembership } =
        useContext(MembershipsContext);
    const [membership, setMembership] = useState<IMembership | null>(null);
    const { membershipId } = useParams<{ membershipId: string }>();

    useEffect(() => {
        (async () => {
            if (membershipId) {
                setMembership(await fetchMembership(parseInt(membershipId)));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [membershipId]);

    const onSubmitUpdate = useCallback(
        async (newData: Partial<IMembership>): Promise<void> => {
            if (membershipId) {
                setMembership(await updateMembership(parseInt(membershipId), newData));
            }
        },
        [updateMembership, membershipId]
    );

    const onAcceptTerminate = useCallback(
        async (membershipId: number): Promise<void> => {
            setMembership(await terminateMembership(membershipId));
        },
        [terminateMembership]
    );

    const onAcceptReactivate = useCallback(
        async (membershipId: number): Promise<void> => {
            setMembership(await reactivateMembership(membershipId));
        },
        [reactivateMembership]
    );

    if (!membership) {
        return (
            <div className="contentWrapper">
                <CircularProgress size="50px" />
            </div>
        );
    }

    return (
        <PageWithTabs
            titleTag="membershipInformation"
            overTabsContent={
                <Grid container flex="col" justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <Typography variant="body2" marginBottom="20px">
                            {membership.organization.name} {prettifyOrgNumber(membership.organization.org_nr)}
                        </Typography>
                    </Grid>
                </Grid>
            }
            tabs={[
                {
                    tag: "details",
                    handle: "details",
                    content: (
                        <MembershipInfoColumn
                            membership={membership}
                            onSubmitUpdate={onSubmitUpdate}
                            onAcceptTerminate={onAcceptTerminate}
                            onAcceptReactivate={onAcceptReactivate}
                        />
                    ),
                },
                {
                    tag: "invoiceOrders",
                    handle: "invoice-orders",
                    content: <InvoicesContent membershipId={membership.id} />,
                    requiredPerm: "view_invoice",
                },
                /* Add back in KP-2142
                { 
                    tag: "statistics",
                    handle: "stats",
                    content: <MembershipStats membership={membership} />,
                    requiredPerm: "view_accessystatistics",
                },
                */
                {
                    tag: "adjustments",
                    handle: "adjustments",
                    content: (
                        <MembershipAdjustmentsContent membershipId={membership.id} disabled={!!membership.end_date} />
                    ),
                    requiredPerm: "add_invoiceorder",
                },
            ]}
        />
    );
};

interface IInvoicesContentProps {
    membershipId: number;
}

const InvoicesContent: FC<IInvoicesContentProps> = ({ membershipId }) => {
    const pagination = usePaginationStore("invoice-orders", undefined, {
        membership: membershipId.toString(),
        page_size: 10,
        default_page_size: 10,
        ordering: "-created",
    });

    return <InvoiceOrdersTable pagination={pagination} includeCustomerData={false} />;
};
